import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TableContainer from '@mui/material/TableContainer';

import { fDate } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { EmployerReplacement } from 'src/services/presentations/presentations.types';
import { useGetEmployerReplacementsQuery, useSaveEmployerReplacementMutation, useDeleteEmployerReplacementMutation } from 'src/services/presentations/presentations.service';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

/**
 * The agency's employer wording for anonymised presentations (JT1-1887), on Settings → Brand.
 * Recruiters add employers from the anonymisation review in the builder; admins add, change and
 * remove them here (staging feedback).
 */
export default function EmployerReplacementsCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslate();
  const { currentData: rows = [], isFetching } = useGetEmployerReplacementsQuery();
  const [remove, { isLoading: removing }] = useDeleteEmployerReplacementMutation();
  // An entry to change, or `true` for a new one.
  const [editing, setEditing] = useState<EmployerReplacement | true | null>(null);
  const [removingRow, setRemovingRow] = useState<EmployerReplacement | null>(null);

  const handleRemove = async () => {
    if (!removingRow) return;
    try {
      await remove(removingRow.id).unwrap();
      enqueueSnackbar(t('presentations.employers.removed'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
    setRemovingRow(null);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={t('presentations.employers.title')}
        subheader={t('presentations.employers.subheader')}
        action={canManage && (
          <Button variant="outlined" color="inherit" size="small" startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => setEditing(true)}>
            {t('presentations.employers.add')}
          </Button>
        )}
      />
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('presentations.employers.employer')}</TableCell>
              <TableCell>{t('presentations.employers.replacement')}</TableCell>
              <TableCell>{t('presentations.employers.updated')}</TableCell>
              {canManage && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.employer}</TableCell>
                <TableCell>{row.replacement}</TableCell>
                <TableCell>{[fDate(row.updated_at), [row.updated_by?.first_name, row.updated_by?.last_name].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</TableCell>
                {canManage && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" aria-label={t('presentations.employers.edit', { employer: row.employer })} onClick={() => setEditing(row)}>
                      <Iconify icon="solar:pen-bold" width={18} />
                    </IconButton>
                    <IconButton size="small" aria-label={t('presentations.employers.remove', { employer: row.employer })} onClick={() => setRemovingRow(row)}>
                      <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && !isFetching && (
              <TableRow><TableCell colSpan={canManage ? 4 : 3}>{t('presentations.employers.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editing && <WordingDialog entry={editing === true ? null : editing} onClose={() => setEditing(null)} />}

      <ConfirmDialog
        open={!!removingRow}
        onClose={() => setRemovingRow(null)}
        title={t('presentations.employers.remove_title', { employer: removingRow?.employer ?? '' })}
        content={t('presentations.employers.remove_body')}
        action={<Button variant="contained" color="error" disabled={removing} onClick={handleRemove}>{t('common.delete')}</Button>}
      />
    </Card>
  );
}

/** Adds an employer's wording, or changes it; the employer of an existing entry stays as it is. */
function WordingDialog({ entry, onClose }: { entry: EmployerReplacement | null; onClose: VoidFunction }) {
  const { t } = useTranslate();
  const [save, { isLoading: saving }] = useSaveEmployerReplacementMutation();
  const [employer, setEmployer] = useState(entry?.employer ?? '');
  const [replacement, setReplacement] = useState(entry?.replacement ?? '');

  const submit = async () => {
    try {
      await save({ employer: employer.trim(), replacement: replacement.trim() }).unwrap();
      enqueueSnackbar(t('presentations.employers.saved', { employer: employer.trim() }), { variant: 'success' });
      onClose();
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t(entry ? 'presentations.employers.edit_title' : 'presentations.employers.add_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label={t('presentations.employers.employer')} value={employer} onChange={(event) => setEmployer(event.target.value)} disabled={!!entry} autoFocus={!entry} inputProps={{ maxLength: 200 }} />
          <TextField
            label={t('presentations.employers.replacement')}
            value={replacement}
            onChange={(event) => setReplacement(event.target.value)}
            autoFocus={!!entry}
            helperText={t('presentations.employers.replacement_helper')}
            inputProps={{ maxLength: 200 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={saving} disabled={!employer.trim() || !replacement.trim()} onClick={submit}>
          {t('common.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
